import type { Client, ClientHeaders } from "@medusajs/js-sdk";

/** Per-line classification in a reorder result. */
export type StoreReorderLineStatus = "available" | "repriced" | "unavailable";

/** Why an `unavailable` line could not be re-added. */
export type StoreReorderUnavailableReason =
  | "discontinued"
  | "out_of_stock"
  | "inactive";

/**
 * One resolved line of a reorder. Prices are in the order currency's major
 * units (matching order line items / `convertToLocale`).
 */
export interface StoreReorderLine {
  status: StoreReorderLineStatus;
  variant_id: string | null;
  title: string;
  thumbnail?: string | null;
  quantity: number;
  /** present on `available` / `repriced` */
  offer_id?: string;
  /** current unit price - on `available` */
  unit_price?: number;
  /** original -> current unit price - on `repriced` */
  old_price?: number;
  new_price?: number;
  /** why the line could not be re-added - on `unavailable` */
  reason?: StoreReorderUnavailableReason;
}

/** A buyer's per-line pick + quantity override, keyed by `variant_id`. */
export interface StoreReorderLineSelection {
  variant_id: string;
  quantity: number;
}

export interface StoreReorderRequest {
  order_id: string;
  /**
   * Preview only (`true`) or add the resolved lines to the cart (`false`).
   * Defaults to `true` server-side.
   */
  dry_run?: boolean;
  /**
   * Optional per-line pick + quantity override, applied on confirm
   * (`dry_run: false`) only. Omitted adds every addable line at its ordered
   * quantity - the pre-existing all-in behavior. See ADR-0004.
   */
  selections?: StoreReorderLineSelection[];
}

export interface StoreReorderResponse {
  /** The seller cart lines were added to - only present after a confirm. */
  cart_id?: string;
  seller: { id: string; name: string } | null;
  lines: StoreReorderLine[];
}

/**
 * Client for the buyer reorder endpoint (`POST /store/reorder`).
 *
 * Two-phase: {@link Reorder.preview} resolves a past order against current
 * offers and returns the changed-line diff without mutating any cart (for the
 * review dialog); {@link Reorder.confirm} adds the available + repriced lines to
 * the customer's seller cart and returns the affected `cart_id`.
 */
export class Reorder {
  private client: Client;

  constructor(client: Client) {
    this.client = client;
  }

  /**
   * Resolves a past order against live offers WITHOUT touching the cart -
   * drives the review-before-confirm step.
   */
  preview = async (
    orderId: string,
    headers?: ClientHeaders
  ): Promise<StoreReorderResponse> =>
    this.request({ order_id: orderId, dry_run: true }, headers);

  /**
   * Adds the available + repriced lines of a past order to the customer's
   * seller cart and returns the affected `cart_id`. `selections`, when
   * given, narrows the add to a buyer-picked subset at buyer-picked
   * quantities instead of every addable line at its ordered quantity.
   */
  confirm = async (
    orderId: string,
    selections?: StoreReorderLineSelection[],
    headers?: ClientHeaders
  ): Promise<StoreReorderResponse> =>
    this.request({ order_id: orderId, dry_run: false, selections }, headers);

  private request = async (
    body: StoreReorderRequest,
    headers?: ClientHeaders
  ): Promise<StoreReorderResponse> =>
    this.client.fetch<StoreReorderResponse>("/store/reorder", {
      method: "POST",
      body,
      headers
    });
}
